/**
 * Mock onboarding review-request detail data.
 * Keyed by request ID so it can be replaced with API responses later.
 *
 * Expected API shape (future):
 * GET /onboarding/requests/:id
 * → {
 *   id, candidate_name, designation, store_code, store_name, status,
 *   sections: { jobDetails, personal, address, education, employment, bank, insurance, declarations },
 *   documents: {
 *     personal: [{ id, name, type, size, url }],
 *     address: [], ...
 *   }
 * }
 */

import { baseJobDetails } from "./jobDetailsConfig";

const basePersonal = {
  aadhar_number: "3426-6789-4521",
  aadhar_verified_at: "12 Jun 2024, 10:42 AM",
  first_name: "",
  middle_name: "",
  last_name: "",
  father_first_name: "Ramesh",
  father_middle_name: "",
  father_last_name: "Mehta",
  date_of_birth: "15 Mar 1998",
  gender: "Male",
  phone_number: "+91 98765 43210",
  alternative_phone_number: "",
  email_address: "",
  marital_status: "Single",
  nationality: "Indian",
};

const baseAddress = {
  permanent_address_house: "12, 4th Cross",
  permanent_address_street: "Indiranagar",
  permanent_address_area: "",
  permanent_address_landmark: "Near Metro Station",
  permanent_address_city: "Bengaluru",
  permanent_address_state: "Karnataka",
  permanent_address_pincode: "560038",
  current_address_house: "12, 4th Cross",
  current_address_street: "Indiranagar",
  current_address_area: "",
  current_address_landmark: "Near Metro Station",
  current_address_city: "Bengaluru",
  current_address_state: "Karnataka",
  current_address_pincode: "560038",
  address_flag: true,
  same_as_current: true,
};

const baseEducation = {
  highest_qualification: "graduate",
  records: [
    {
      qualification: "graduate",
      degree: "Bachelor of Commerce",
      start_date: "01 Jun 2016",
      graduation_date: "31 May 2019",
      university: "Bangalore University",
      college: "St. Joseph's College of Commerce",
      cgpa_percentage: "7.2",
    },
    {
      qualification: "12th",
      degree: "",
      start_date: "01 Jun 2014",
      graduation_date: "31 Mar 2016",
      university: "Karnataka State Board",
      college: "ABC PU College",
      cgpa_percentage: "78",
    },
    {
      qualification: "10th",
      degree: "",
      start_date: "01 Jun 2012",
      graduation_date: "31 Mar 2014",
      university: "Karnataka State Board",
      college: "ABC High School",
      cgpa_percentage: "82",
    },
  ],
};

const buildEducationSection = (override) => {
  if (!override) {
    return {
      highest_qualification: baseEducation.highest_qualification,
      records: baseEducation.records.map((record) => ({ ...record })),
    };
  }

  if (Array.isArray(override.records)) {
    return {
      highest_qualification:
        override.highest_qualification ||
        override.records[0]?.qualification ||
        baseEducation.highest_qualification,
      records: override.records.map((record) => ({ ...record })),
    };
  }

  if (Array.isArray(override)) {
    return {
      highest_qualification:
        override[0]?.qualification || baseEducation.highest_qualification,
      records: override.map((record) => ({ ...record })),
    };
  }

  // Legacy flat object
  if (typeof override === "object") {
    return {
      highest_qualification: "graduate",
      records: [
        {
          qualification: "graduate",
          degree: override.highest_qualification || override.degree || "",
          start_date: override.start_date || "",
          graduation_date:
            override.graduation_date || override.year_of_passing || "",
          university:
            override.university || override.institution_name || "",
          college: override.college || "",
          cgpa_percentage:
            override.cgpa_percentage || override.percentage || "",
        },
      ],
    };
  }

  return {
    highest_qualification: baseEducation.highest_qualification,
    records: baseEducation.records.map((record) => ({ ...record })),
  };
};

const baseEmployment = {
  uan: "100234567890",
  records: [
    {
      company: "ABC Company",
      designation: "Sales Executive",
      start_date: "01 Mar 2022",
      last_date: "15 Jul 2024",
      is_last_experience: true,
    },
    {
      company: "XYZ Company",
      designation: "Associate",
      start_date: "15 Jun 2019",
      last_date: "28 Feb 2022",
      is_last_experience: false,
    },
  ],
};

const buildEmploymentSection = (override) => {
  if (!override) {
    return {
      uan: baseEmployment.uan,
      records: baseEmployment.records.map((record) => ({ ...record })),
    };
  }

  // New shape: { uan, records: [...] }
  if (Array.isArray(override.records)) {
    return {
      uan: override.uan || baseEmployment.uan,
      records: override.records.map((record) => ({ ...record })),
    };
  }

  // Legacy array of experience rows (may include repeated uan)
  if (Array.isArray(override)) {
    const uan =
      override.find((record) => record?.uan)?.uan || baseEmployment.uan;
    return {
      uan,
      records: override.map(
        ({ uan: _uan, current_check: _current, ...record }) => ({ ...record }),
      ),
    };
  }

  // Legacy single object
  if (typeof override === "object") {
    const companyFromLocation =
      typeof override.work_location === "string" &&
      override.work_location.includes(" - ")
        ? override.work_location.split(" - ").slice(1).join(" - ").trim()
        : override.company || baseEmployment.records[0].company;
    return {
      uan: override.uan || baseEmployment.uan,
      records: [
        {
          company: companyFromLocation,
          designation:
            override.designation || baseEmployment.records[0].designation,
          start_date: override.start_date || "",
          last_date: override.last_date || "",
          is_last_experience: override.is_last_experience ?? true,
        },
      ],
    };
  }

  return {
    uan: baseEmployment.uan,
    records: baseEmployment.records.map((record) => ({ ...record })),
  };
};

const baseBank = {
  account_holder_name: "",
  name_as_per_bank: "",
  pan_number: "ABCDE1234F",
  pan_verified_at: "12 Jun 2024, 11:05 AM",
  account_number: "1234567890",
  bank_verified_at: "12 Jun 2024, 11:08 AM",
  ifsc_code: "HDFC0001234",
  account_type: "Savings",
  bank_name: "HDFC Bank",
  branch_name: "Indiranagar",
  branch_address: "100 Feet Road, Indiranagar, Bengaluru",
};

const baseInsurance = {
  nominee_full_name: "Ramesh Mehta",
  nominee_relationship: "Father",
  nominee_dob: "15 Mar 1965",
  emergency_relationship: "Spouse",
  emergency_full_name: "Sunil Mehta",
  emergency_phone: "9876543210",
  emergency_email: "sunil.mehta@gmail.com",
};

const baseDeclarations = {
  code_of_conduct: "Yes",
  code_of_conduct_verified_at: "20 Jul 2024, 10:15 AM",
  it_policy: "Yes",
  it_policy_verified_at: "20 Jul 2024, 10:16 AM",
  posh_policy: "Yes",
  posh_policy_verified_at: "20 Jul 2024, 10:17 AM",
};

const buildSections = (overrides = {}) => ({
  jobDetails: { ...baseJobDetails, ...(overrides.jobDetails || {}) },
  personal: { ...basePersonal, ...(overrides.personal || {}) },
  address: { ...baseAddress, ...(overrides.address || {}) },
  education: buildEducationSection(overrides.education),
  employment: buildEmploymentSection(overrides.employment),
  bank: { ...baseBank, ...(overrides.bank || {}) },
  insurance: { ...baseInsurance, ...(overrides.insurance || {}) },
  declarations: { ...baseDeclarations, ...(overrides.declarations || {}) },
});

const buildDocuments = (overrides = {}) => ({
  jobDetails: overrides.jobDetails || [],
  personal: overrides.personal || [],
  address: overrides.address || [],
  education: overrides.education || [],
  employment: overrides.employment || [],
  bank: overrides.bank || [],
  insurance: overrides.insurance || [],
  declarations: overrides.declarations || [],
});

const SAMPLE_PDF = "mock-docs/sample.pdf";
const SAMPLE_PHOTO = "mock-docs/sample-photo.png";

const defaultDocuments = buildDocuments({
  personal: [
    {
      id: "doc-aadhar",
      name: "Aadhar_Card.pdf",
      type: "pdf",
      size: "245 KB",
      url: SAMPLE_PDF,
    },
    {
      id: "doc-photo",
      name: "Passport_Photo.jpg",
      type: "img",
      size: "120 KB",
      url: SAMPLE_PHOTO,
    },
  ],
  address: [
    {
      id: "doc-address",
      name: "Address_Proof.pdf",
      type: "pdf",
      size: "310 KB",
      url: SAMPLE_PDF,
    },
  ],
  education: [
    {
      id: "doc-degree",
      name: "Degree_Certificate.pdf",
      type: "pdf",
      size: "420 KB",
      url: SAMPLE_PDF,
    },
  ],
  bank: [
    {
      id: "doc-cheque",
      name: "Cancelled_Cheque.pdf",
      type: "pdf",
      size: "180 KB",
      url: SAMPLE_PDF,
    },
  ],
});

const buildRequest = (meta, sectionOverrides = {}, documentOverrides) => ({
  ...meta,
  sections: buildSections(sectionOverrides),
  documents: documentOverrides
    ? buildDocuments(documentOverrides)
    : defaultDocuments,
});

/**
 * Mock detail payloads for each queue request.
 * Missing IDs fall back to a generic payload built from queue list metadata.
 */
export const reviewRequestDetailsById = {
  "REQ-24002": buildRequest(
    {
      id: "REQ-24002",
      candidate_name: "Ananya Mehta",
      designation: "Cashier",
      store_code: "IND008",
      store_name: "More Supermarket, Indiranagar",
      status: "pending",
    },
    {
      personal: {
        first_name: "Ananya",
        last_name: "Mehta",
        father_first_name: "Ramesh",
        father_last_name: "Mehta",
        email_address: "ananya.mehta@gmail.com",
        alternative_phone_number: "+91 90000 11223",
        gender: "Female",
      },
      employment: {
        uan: "100111222333",
        records: [
          {
            company: "ABC Retail Pvt Ltd",
            designation: "Sales Associate",
            start_date: "10 Jan 2022",
            last_date: "30 Jun 2024",
            is_last_experience: true,
          },
          {
            company: "XYZ Traders",
            designation: "Cashier",
            start_date: "01 Mar 2019",
            last_date: "31 Dec 2021",
            is_last_experience: false,
          },
        ],
      },
      bank: {
        account_holder_name: "Ananya Mehta",
        name_as_per_bank: "Ananya Mehta",
      },
    },
  ),
  "REQ-24001": buildRequest(
    {
      id: "REQ-24001",
      candidate_name: "Deepak Kumar",
      designation: "Floor Supervisor",
      store_code: "BLR021",
      store_name: "More Hyper, Koramangala",
      status: "in_progress",
    },
    {
      personal: {
        first_name: "Deepak",
        last_name: "Kumar",
        father_first_name: "Suresh",
        father_last_name: "Kumar",
        gender: "Male",
        phone_number: "+91 99887 76655",
        email_address: "deepak.kumar@gmail.com",
      },
      employment: {
        uan: "100998877665",
        records: [
          {
            company: "ABC Company",
            designation: "Floor Supervisor",
            start_date: "15 Mar 2021",
            last_date: "28 Feb 2024",
            is_last_experience: true,
          },
          {
            company: "XYZ Company",
            designation: "Team Lead",
            start_date: "01 Feb 2018",
            last_date: "14 Mar 2021",
            is_last_experience: false,
          },
        ],
      },
      bank: {
        account_holder_name: "Deepak Kumar",
        name_as_per_bank: "Deepak Kumar",
      },
    },
  ),
  "REQ-23998": buildRequest(
    {
      id: "REQ-23998",
      candidate_name: "Priya Sharma",
      designation: "Cashier",
      store_code: "HYD014",
      store_name: "More Supermarket, Banjara Hills",
      status: "awaiting",
    },
    {
      personal: {
        first_name: "Priya",
        last_name: "Sharma",
        gender: "Female",
        phone_number: "+91 91234 56789",
        email_address: "priya.sharma@gmail.com",
      },
      employment: {
        designation: "Cashier",
        work_location: "HYD014 - More Supermarket, Banjara Hills",
      },
      bank: {
        account_holder_name: "Priya Sharma",
        name_as_per_bank: "Priya Sharma",
      },
    },
  ),
  "REQ-23995": buildRequest(
    {
      id: "REQ-23995",
      candidate_name: "Rohit Verma",
      designation: "Inventory Associate",
      store_code: "DEL003",
      store_name: "More Hyper, Saket",
      status: "pending",
    },
    {
      personal: {
        first_name: "Rohit",
        last_name: "Verma",
        father_first_name: "Ajay",
        father_last_name: "Verma",
        email_address: "rohit.verma@gmail.com",
      },
      employment: {
        designation: "Inventory Associate",
        work_location: "DEL003 - More Hyper, Saket",
      },
      bank: {
        account_holder_name: "Rohit Verma",
        name_as_per_bank: "Rohit Verma",
      },
    },
  ),
  "REQ-23990": buildRequest(
    {
      id: "REQ-23990",
      candidate_name: "Sneha Reddy",
      designation: "Sales Associate",
      store_code: "MUM011",
      store_name: "More Supermarket, Andheri West",
      status: "completed",
    },
    {
      personal: {
        first_name: "Sneha",
        last_name: "Reddy",
        gender: "Female",
        phone_number: "+91 90000 11122",
        email_address: "sneha.reddy@gmail.com",
      },
      employment: {
        designation: "Sales Associate",
        work_location: "MUM011 - More Supermarket, Andheri West",
      },
      bank: {
        account_holder_name: "Sneha Reddy",
        name_as_per_bank: "Sneha Reddy",
      },
    },
  ),
  "REQ-23988": buildRequest(
    {
      id: "REQ-23988",
      candidate_name: "Arjun Nair",
      designation: "Cashier",
      store_code: "BLR021",
      store_name: "More Hyper, Koramangala",
      status: "in_progress",
    },
    {
      personal: {
        first_name: "Arjun",
        last_name: "Nair",
        father_first_name: "Mohan",
        father_last_name: "Nair",
        email_address: "arjun.nair@gmail.com",
      },
      employment: {
        designation: "Cashier",
        work_location: "BLR021 - More Hyper, Koramangala",
      },
      bank: {
        account_holder_name: "Arjun Nair",
        name_as_per_bank: "Arjun Nair",
      },
    },
  ),
  "REQ-23985": buildRequest(
    {
      id: "REQ-23985",
      candidate_name: "Kavya Iyer",
      designation: "Floor Supervisor",
      store_code: "IND008",
      store_name: "More Supermarket, Indiranagar",
      status: "awaiting",
    },
    {
      personal: {
        first_name: "Kavya",
        last_name: "Iyer",
        gender: "Female",
        email_address: "kavya.iyer@gmail.com",
      },
      employment: {
        designation: "Floor Supervisor",
        work_location: "IND008 - More Supermarket, Indiranagar",
      },
      bank: {
        account_holder_name: "Kavya Iyer",
        name_as_per_bank: "Kavya Iyer",
      },
    },
    {
      // fewer docs for this request
      personal: [
        {
          id: "doc-aadhar-k",
          name: "Aadhar_Card.pdf",
          type: "pdf",
          size: "210 KB",
          url: SAMPLE_PDF,
        },
      ],
      address: [],
      education: [],
      employment: [],
      bank: [],
      insurance: [],
      declarations: [],
    },
  ),
  "REQ-23980": buildRequest(
    {
      id: "REQ-23980",
      candidate_name: "Mohit Singh",
      designation: "Inventory Associate",
      store_code: "DEL003",
      store_name: "More Hyper, Saket",
      status: "completed",
    },
    {
      personal: {
        first_name: "Mohit",
        last_name: "Singh",
        email_address: "mohit.singh@gmail.com",
      },
      employment: {
        designation: "Inventory Associate",
        work_location: "DEL003 - More Hyper, Saket",
      },
      bank: {
        account_holder_name: "Mohit Singh",
        name_as_per_bank: "Mohit Singh",
      },
    },
  ),
  "REQ-24003": buildRequest(
    {
      id: "REQ-24003",
      candidate_name: "Neha Kapoor",
      designation: "Sales Associate",
      store_code: "BLR021",
      store_name: "More Hyper, Koramangala",
      status: "resubmitted",
      // Fields the candidate corrected after send-back (mock until API exists)
      // `remark` is the previous flag comment from the send-back review
      updatedFields: [
        {
          sectionKey: "personal",
          fieldKey: "email_address",
          remark: "Email looks incorrect. Please provide a valid personal email.",
        },
        {
          sectionKey: "personal",
          fieldKey: "alternative_phone_number",
          remark: "Alternate number is same as primary. Please update.",
        },
        {
          sectionKey: "address",
          fieldKey: "current_address_house",
          remark: "Address line is incomplete. Add landmark / house number.",
        },
        {
          sectionKey: "address",
          fieldKey: "current_address_pincode",
          remark: "Pincode does not match the address locality.",
        },
        {
          sectionKey: "bank",
          fieldKey: "account_number",
          remark: "Account number digits look incorrect. Please verify and re-enter.",
        },
      ],
    },
    {
      personal: {
        first_name: "Neha",
        last_name: "Kapoor",
        father_first_name: "Rajesh",
        father_last_name: "Kapoor",
        gender: "Female",
        aadhar_number: "3426-6789-4521",
        phone_number: "+91 98765 43210",
        alternative_phone_number: "+91 90000 11223",
        email_address: "neha.kapoor.updated@gmail.com",
      },
      address: {
        current_address_house: "42, 7th Main",
        current_address_street: "Koramangala",
        current_address_pincode: "560034",
        address_flag: false,
        same_as_current: false,
        permanent_address_house: "12, 4th Cross",
        permanent_address_street: "Indiranagar",
        permanent_address_pincode: "560038",
      },
      employment: {
        designation: "Sales Associate",
        work_location: "BLR021 - More Hyper, Koramangala",
      },
      bank: {
        account_holder_name: "Neha Kapoor",
        name_as_per_bank: "Neha Kapoor",
        account_number: "50100234567890",
      },
    },
  ),
};

/** Field labels per section — used to render form inputs from mock/API data */
export const REVIEW_SECTION_FIELDS = {
  personal: [
    { key: "aadhar_number", label: "Aadhar Number" },
    {
      key: "aadhar_verified_at",
      label: "Aadhar Verified At",
      isSystem: true,
    },
    { key: "first_name", label: "First Name", isRequired: true, isAi: true },
    { key: "middle_name", label: "Middle Name", isAi: true },
    { key: "last_name", label: "Last Name", isRequired: true, isAi: true },
    { key: "father_first_name", label: "Father's First Name", isAi: true },
    { key: "father_middle_name", label: "Father's Middle Name", isAi: true },
    { key: "father_last_name", label: "Father's Last Name", isAi: true },
    { key: "date_of_birth", label: "Date of Birth", isRequired: true, isAi: true },
    { key: "gender", label: "Gender", isRequired: true, isAi: true },
    {
      key: "phone_number",
      label: "Phone Number",
      isRequired: true,
      isSystem: true,
    },
    { key: "alternative_phone_number", label: "Alternative Phone Number" },
    { key: "email_address", label: "Email Address", isRequired: true },
    { key: "marital_status", label: "Marital Status" },
    { key: "nationality", label: "Nationality", isAi: true },
  ],
  address: [
    { type: "heading", label: "Permanent Address" },
    {
      key: "permanent_address_house",
      label: "Flat/House/Building",
      isRequired: true,
      fullWidth: true,
    },
    { key: "permanent_address_street", label: "Street", fullWidth: true },
    { key: "permanent_address_area", label: "Area", fullWidth: true },
    { key: "permanent_address_landmark", label: "Landmark", fullWidth: true },
    {
      key: "permanent_address_city",
      label: "City/Town/Village",
      isRequired: true,
      rowStart: true,
    },
    {
      key: "permanent_address_state",
      label: "State",
      isRequired: true,
      rowStart: true,
    },
    { key: "permanent_address_pincode", label: "PIN Code", isRequired: true },
    { type: "heading", label: "Current Address" },
    {
      key: "current_address_house",
      label: "Flat/House/Building",
      isRequired: true,
      fullWidth: true,
    },
    { key: "current_address_street", label: "Street", fullWidth: true },
    { key: "current_address_area", label: "Area", fullWidth: true },
    { key: "current_address_landmark", label: "Landmark", fullWidth: true },
    {
      key: "current_address_city",
      label: "City/Town/Village",
      isRequired: true,
      rowStart: true,
    },
    {
      key: "current_address_state",
      label: "State",
      isRequired: true,
      rowStart: true,
    },
    { key: "current_address_pincode", label: "PIN Code", isRequired: true },
  ],
  education: [
    { key: "qualification", label: "Qualification", isRequired: true },
    { key: "degree", label: "Degree" },
    { key: "start_date", label: "Start Date", isRequired: true },
    { key: "graduation_date", label: "Graduation Date", isRequired: true },
    { key: "university", label: "University", isRequired: true },
    { key: "college", label: "College/Institute" },
    { key: "cgpa_percentage", label: "CGPA/Percentage" },
    { key: "maximum_cgpa_percentage", label: "Maximum CGPA/Percentage" },
  ],
  educationShared: [
    { key: "highest_qualification", label: "Highest Qualification" },
  ],
  employment: [
    { key: "company", label: "Company Name", isRequired: true },
    { key: "designation", label: "Designation", isRequired: true },
    { key: "start_date", label: "Start Date", isRequired: true },
    { key: "last_date", label: "End Date" },
  ],
  employmentShared: [
    { key: "uan", label: "UAN" },
    {
      key: "uan_verified_at",
      label: "UAN Verified At",
      isSystem: true,
    },
  ],
  bank: [
    {
      key: "account_holder_name",
      label: "Name as per PAN",
      isRequired: true,
      isAi: true,
    },
    {
      key: "name_as_per_bank",
      label: "Name as per Bank",
      isRequired: true,
      isAi: true,
    },
    {
      key: "pan_number",
      label: "PAN",
      isRequired: true,
    },
    {
      key: "pan_verified_at",
      label: "PAN Verified At",
      isSystem: true,
    },
    {
      key: "account_number",
      label: "Bank Account Number",
      isRequired: true,
    },
    {
      key: "bank_verified_at",
      label: "Bank Verified At",
      isSystem: true,
    },
    { key: "ifsc_code", label: "IFSC Code", isRequired: true },
    { key: "account_type", label: "Account Type", isRequired: true },
    { key: "bank_name", label: "Bank Name", isRequired: true, isAi: true },
    { key: "branch_name", label: "Branch Name", isAi: true },
    {
      key: "branch_address",
      label: "Branch Address",
      isAi: true,
      isMultiline: true,
    },
  ],
  insurance: [
    { key: "blood_group", label: "Blood Group" },
    { type: "heading", label: "Nischint" },
    {
      key: "nominee_full_name",
      label: "Nominee's Full Name",
      isRequired: true,
    },
    {
      key: "nominee_relationship",
      label: "Relationship with Nominee",
      isRequired: true,
    },
    {
      key: "nominee_dob",
      label: "Nominee's Date of Birth",
      isRequired: true,
    },
    { type: "heading", label: "Emergency Contact" },
    {
      key: "emergency_relationship",
      label: "Relationship",
      isRequired: true,
    },
    {
      key: "emergency_full_name",
      label: "Full Name",
      isRequired: true,
    },
    {
      key: "emergency_phone",
      label: "Phone Number",
      isRequired: true,
    },
    {
      key: "emergency_email",
      label: "Email Address",
      isRequired: true,
    },
  ],
  declarations: [
    { key: "code_of_conduct", label: "Code of Conduct" },
    {
      key: "code_of_conduct_verified_at",
      label: "Code of Conduct Verified At",
      isSystem: true,
    },
    { key: "it_policy", label: "IT Policy" },
    {
      key: "it_policy_verified_at",
      label: "IT Policy Verified At",
      isSystem: true,
    },
    { key: "posh_policy", label: "POSH Policy" },
    {
      key: "posh_policy_verified_at",
      label: "POSH Policy Verified At",
      isSystem: true,
    },
  ],
};

/**
 * Fetch mock review request detail by ID.
 * Replace this helper with an API call later.
 */
export const getReviewRequestById = (requestId) => {
  if (reviewRequestDetailsById[requestId]) {
    return reviewRequestDetailsById[requestId];
  }

  return {
    id: requestId,
    candidate_name: "Unknown Candidate",
    designation: "-",
    store_code: "-",
    store_name: "-",
    status: "pending",
    sections: buildSections(),
    documents: buildDocuments(),
  };
};
